<template>
  <form>
    <FormHeader :title="title" @cancel="onCancel" @submit="onSubmit"/>
    <div class="space-y-12 mt-5">
      <div>
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Détails du bail de location") }}</h3>
        <FormDateField v-model="signatureDate" field-path="attributes.signatureDate" :field-label="$gettext('Date de signature')" :field-attr="signatureDateAttrs" :errors="errors" />
        <FormSelectField v-model="leaseTypeId" field-path="relationships.leaseType.data.id" :field-label="$gettext('Type de bail')" :field-attrs="leaseTypeIdAttrs" :errors="errors" :options="leaseTypeOptions" />
        <FormMoneyField v-model="price" field-path="attributes.price" :field-label="$gettext('Montant du loyer')" :field-attrs="priceAttrs" :errors="errors" />
        <FormDateField v-model="endDate" field-path="attributes.endDate" :field-label="$gettext('Date de fin')" :field-attr="endDateAttrs" :errors="errors" />
        <FormCustomerComboboxField v-model="tenants" field-path="relationships.tenants.data" :field-label="$gettext('Locataire(s)')" :field-attrs="tenantsAttrs" :errors="errors"/>

        <div class="mt-8 rounded-lg border border-gray-300 bg-white p-6 shadow-sm">
          <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Questions permettant de déterminer le profil de risque de la transaction") }}</h3>

          <UnusualClientCharacteristicsBlock
            v-model:characteristics="unusualClientCharacteristics"
            v-model:reasons="unusualClientReasons"
            v-model:explanation="unusualClientExplanation"
            :characteristicsAttrs="unusualClientCharacteristicsAttrs"
            :explanationAttrs="unusualClientExplanationAttrs"
            :errors="errors"
          />
        </div>
      </div>

      <div>
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Documents à importer") }}</h3>
        <FormDocumentsField
          v-model="leaseAgreementDocuments"
          category-name="signedRentalListingAgreement"
          field-path="relationships.leaseAgreementDocuments.data"
          :field-label="$gettext('Bail de location signé')"
          field-description=""
          :field-attrs="leaseAgreementDocumentsAttrs" :errors="errors"/>
      </div>
    </div>
    <FormFooter @cancel="onCancel" @submit="onSubmit"/>
  </form>
</template>


<script setup>
import { useForm } from "vee-validate";
import * as yup from "yup";
import { watch, computed } from "vue";
import FormHeader from "@/components/form-fields/FormHeader.vue";
import FormFooter from "@/components/form-fields/FormFooter.vue";
import { useGettext } from "vue3-gettext";
import FormCustomerComboboxField from "../form-fields/FormCustomerComboboxField.vue";
import FormMoneyField from "../form-fields/FormMoneyField.vue";
import FormDateField from "../form-fields/FormDateField.vue";
import FormSelectField from "../form-fields/FormSelectField.vue";
import { useLeaseTypeStore } from "@/stores/leaseType";
import FormDocumentsField from "../form-fields/FormDocumentsField.vue";
import UnusualClientCharacteristicsBlock from "@/components/unusual-client/UnusualClientCharacteristicsBlock.vue";

const { $gettext } = useGettext();
const props = defineProps(["leaseAgreement", "title"]);
const emits = defineEmits(["submit", "cancel"]);
const leaseTypeStore = useLeaseTypeStore();

const validationSchema = yup.object({
  attributes: yup.object({
    price: yup.number().required($gettext("est obligatoire")),
    signatureDate: yup.date().required($gettext("est obligatoire")),
    endDate: yup.date().required($gettext("est obligatoire")),
    unusualClientCharacteristics: yup.string().oneOf(["yes", "no"]).nullable(),
    unusualClientReasons: yup
      .array()
      .of(yup.string())
      .when("unusualClientCharacteristics", {
        is: "yes",
        then: (schema) => schema.min(1, $gettext("au moins 1 motif obligatoire")),
        otherwise: (schema) => schema.nullable()
      }),
    unusualClientExplanation: yup.string().nullable()
  }),
  relationships: yup.object({
    tenants: yup.object({
      data: yup.array().of(yup.object({
        id: yup.string()
      })).min(1, $gettext("au moins 1 obligatoire"))
    }),
    leaseType: yup.object({
      data: yup.object({
        id: yup.string().required($gettext("est obligatoire"))
      })
    })
  })
});

const { errors, handleSubmit, defineField, setValues } = useForm({
  validationSchema: validationSchema,
  initialValues: props.leaseAgreement
});

const [price, priceAttrs] = defineField("attributes.price");
const [signatureDate, signatureDateAttrs] = defineField("attributes.signatureDate");
const [endDate, endDateAttrs] = defineField("attributes.endDate");
const [tenants, tenantsAttrs] = defineField("relationships.tenants.data");
const [leaseTypeId, leaseTypeIdAttrs] = defineField("relationships.leaseType.data.id");
const [leaseAgreementDocuments, leaseAgreementDocumentsAttrs] = defineField("relationships.leaseAgreementDocuments.data");
const [unusualClientCharacteristics, unusualClientCharacteristicsAttrs] = defineField("attributes.unusualClientCharacteristics");
const [unusualClientReasons] = defineField("attributes.unusualClientReasons");
const [unusualClientExplanation, unusualClientExplanationAttrs] = defineField("attributes.unusualClientExplanation");

const leaseTypeOptions = computed(() =>
  leaseTypeStore.sortedLeaseTypes.map((leaseType) => ({
    value: leaseType.id,
    label: leaseType.attributes.name
  }))
);


const stripCustomerObjects = (customers) => {
  if (!Array.isArray(customers)) return customers;
  return customers.map(({ id, type }) => ({ id, type }));
};

const onFormSubmit = ((values, actions) => {
  const payload = {
    ...values,
    relationships: {
      ...values.relationships,
      tenants: {
        ...values.relationships?.tenants,
        data: stripCustomerObjects(values.relationships?.tenants?.data)
      }
    }
  };
  emits("submit", payload, actions);
});

const onSubmit = (() => {
  handleSubmit(onFormSubmit)();
});

const onCancel = (() => {
  emits("cancel");
});

watch(() => props.leaseAgreement, (newLeaseAgreement) => {
  if (newLeaseAgreement) setValues(newLeaseAgreement);
}, { immediate: true });
</script>
